import React, {useEffect, useMemo, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment-timezone';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

function getDecisionTone(decision) {
  switch (decision) {
    case 'BUY':
      return {bg: 'rgba(57, 217, 138, 0.12)', color: '#39D98A', border: 'rgba(57, 217, 138, 0.28)'};
    case 'SELL':
      return {bg: 'rgba(255, 107, 107, 0.12)', color: '#FF6B6B', border: 'rgba(255, 107, 107, 0.28)'};
    default:
      return {bg: 'rgba(245, 195, 107, 0.12)', color: '#F5C36B', border: 'rgba(245, 195, 107, 0.28)'};
  }
}

const PredictionDisplay = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const fetchOverview = async () => {
      try {
        const response = await api.get('/dashboard/overview');
        if (active) {
          setOverview(response.data);
        }
      } catch (err) {
        console.error('Error fetching predictions:', err);
        if (active) {
          setError(t('decisions.loadError'));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    fetchOverview();

    return () => {
      active = false;
    };
  }, [t]);

  const grouped = useMemo(() => {
    const assets = overview?.tracked_assets || [];
    return {
      actionable: assets.filter(item => item.confidence_score >= 60),
      watchlist: assets.filter(item => item.confidence_score < 60),
    };
  }, [overview]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 12}}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !overview) {
    return (
      <Container maxWidth="xl" sx={{py: 5}}>
        <Alert severity="error">{error || t('decisions.unavailable')}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{py: { xs: 3, md: 5 }}}>
      <Stack spacing={4}>
        <Paper
          sx={{
            p: {xs: 3, md: 4},
            borderRadius: 5,
            background:
              isDark
                ? 'linear-gradient(160deg, rgba(10,21,39,0.98) 0%, rgba(16,29,51,0.94) 55%, rgba(20,34,57,0.96) 100%)'
                : 'linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(243,248,253,0.96) 55%, rgba(236,243,250,0.98) 100%)',
            border: '1px solid',
            borderColor: 'divider',
          }}>
          <Typography variant="h3" sx={{fontWeight: 800, color: 'text.primary'}}>
            {t('decisions.title')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{mt: 1.5, maxWidth: 720}}>
            {t('decisions.subtitle')}
          </Typography>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={7}>
            <Paper sx={{p: 3.5, borderRadius: 5, background: isDark ? 'rgba(16, 29, 51, 0.84)' : 'rgba(255,255,255,0.88)', border: '1px solid', borderColor: 'divider'}}>
              <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                {t('decisions.highConviction')}
              </Typography>
              <Stack spacing={2}>
                {grouped.actionable.slice(0, 6).map(item => {
                  const tone = getDecisionTone(item.decision);
                  return (
                    <Paper
                      key={item.symbol}
                      sx={{
                        p: 2.5,
                        borderRadius: 4,
                        backgroundColor: isDark ? 'rgba(7,17,31,0.45)' : 'rgba(241,246,252,0.92)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="h6" sx={{fontWeight: 700}}>
                            {item.symbol}
                          </Typography>
                          <Chip
                            label={item.decision}
                            sx={{
                              backgroundColor: tone.bg,
                              color: tone.color,
                              border: `1px solid ${tone.border}`,
                              fontWeight: 700,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {t('decisions.convictionSummary', {
                            label: item.confidence_label,
                            score: item.confidence_score,
                            sentiment: item.sentiment_label,
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.explanations?.join(' ')}
                        </Typography>
                        <Button component={RouterLink} to={`/assets/${item.symbol}`} variant="outlined" color="inherit" sx={{alignSelf: 'flex-start', color: 'text.primary'}}>
                          {t('decisions.openAssetIntelligence')}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack spacing={3}>
              <Paper sx={{p: 3.5, borderRadius: 5, background: isDark ? 'rgba(16, 29, 51, 0.84)' : 'rgba(255,255,255,0.88)', border: '1px solid', borderColor: 'divider'}}>
                <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                  {t('decisions.decisionMix')}
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <Chip label={t('decisions.bullishCount', { count: overview.market_summary.bullish_decisions })} />
                  <Chip label={t('decisions.bearishCount', { count: overview.market_summary.bearish_decisions })} />
                  <Chip label={t('decisions.neutralCount', { count: overview.market_summary.neutral_decisions })} />
                </Stack>
              </Paper>

              <Paper sx={{p: 3.5, borderRadius: 5, background: isDark ? 'rgba(16, 29, 51, 0.84)' : 'rgba(255,255,255,0.88)', border: '1px solid', borderColor: 'divider'}}>
                <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
                  {t('decisions.watchlistSetups')}
                </Typography>
                <Stack spacing={1.5}>
                  {grouped.watchlist.slice(0, 6).map(item => (
                    <Stack key={item.symbol} direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography sx={{fontWeight: 700, color: 'text.primary'}}>{item.symbol}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.confidence_label} · {item.sentiment_label}
                        </Typography>
                      </Box>
                      <Button component={RouterLink} to={`/assets/${item.symbol}`} size="small">
                        {t('decisions.review')}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <Paper sx={{p: 3.5, borderRadius: 5, background: 'rgba(16, 29, 51, 0.84)', border: '1px solid rgba(126, 144, 168, 0.16)'}}>
          <Typography variant="h5" sx={{fontWeight: 700, mb: 2}}>
            {t('decisions.recentUpdates')}
          </Typography>
          <List disablePadding>
            {overview.recent_decisions.map(item => (
              <ListItem
                key={`${item.symbol}-${item.timestamp}`}
                disableGutters
                component={RouterLink}
                to={`/assets/${item.symbol}`}
                sx={{textDecoration: 'none', color: 'inherit'}}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Typography sx={{fontWeight: 700, color: 'text.primary'}}>{item.symbol}</Typography>
                      <Chip size="small" label={item.decision} />
                    </Stack>
                  }
                  secondaryTypographyProps={{ color: 'text.secondary' }}
                  secondary={moment(item.timestamp).locale(i18n.language === 'gr' ? 'el' : 'en').format('DD MMM, HH:mm')}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Stack>
    </Container>
  );
};

export default PredictionDisplay;
